import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ClipboardList, ArrowLeft, Play, CheckCircle2, XCircle, AlertTriangle,
  Warehouse, Package, Scan, Search, Eye, EyeOff, Clock, Filter,
  TrendingDown, TrendingUp, RotateCcw, ChevronRight, Info,
  FileCheck2, History, ShoppingBasket,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime } from '@/lib/api'

// ============================================================
// Types
// ============================================================
type InvItem = {
  id: number
  inventario_id: number
  produto_id: number
  produto_codigo: string | null
  produto_descricao: string | null
  produto_ean: string | null
  produto_unidade: string | null
  produto_endereco: string | null
  quantidade_esperada: string | number
  custo_medio_snapshot: string | number
  quantidade_contada: string | number
  quantidade_final: string | number | null
  status: string
  contador_id: number | null
  bipado_em: string | null
  motivo_divergencia: string | null
  observacao: string | null
  divergencia: number | null
  valor_divergencia: number | null
}

type InvDetalhe = {
  id: number
  codigo: string
  descricao: string | null
  deposito_id: number
  deposito_nome: string | null
  tipo: string
  status: string
  modo_cego: boolean
  total_itens: number
  total_contados: number
  total_divergentes: number
  valor_divergencia: string | number
  iniciado_em: string | null
  finalizado_em: string | null
  aprovado_em: string | null
  cancelado_em: string | null
  created_at: string
  itens: InvItem[]
}

type PreviewResp = {
  produtos_com_ajuste: number
  total_itens: number
  valor_total_divergencia: number
  itens: Array<{
    item_id: number
    produto_id: number
    codigo: string
    descricao: string
    esperada: number
    contada: number
    final: number
    divergencia: number
    valor_divergencia: number
  }>
}

// ============================================================
// Helpers de exibicao
// ============================================================
const STATUS_CFG: Record<string, { label: string; cls: string }> = {
  rascunho:     { label: 'Rascunho',     cls: 'bg-ink-100 text-ink-700' },
  em_contagem:  { label: 'Em contagem',  cls: 'bg-blue-100 text-blue-700' },
  conferencia:  { label: 'Conferência',  cls: 'bg-amber-100 text-amber-700' },
  finalizado:   { label: 'Finalizado',   cls: 'bg-emerald-100 text-emerald-700' },
  cancelado:    { label: 'Cancelado',    cls: 'bg-red-100 text-red-700' },
  aprovado:     { label: 'Aprovado',     cls: 'bg-emerald-100 text-emerald-700' },
}

const ITEM_STATUS_CFG: Record<string, { label: string; cls: string }> = {
  pendente:   { label: 'Pendente',    cls: 'bg-ink-100 text-ink-600' },
  contado:    { label: 'Contado',     cls: 'bg-emerald-100 text-emerald-700' },
  divergente: { label: 'Divergente',  cls: 'bg-amber-100 text-amber-700' },
  aprovado:   { label: 'Aprovado',    cls: 'bg-emerald-100 text-emerald-700' },
}

function num(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0
  return typeof v === 'string' ? parseFloat(v) : v
}

// Toca um feedback sonoro simples via WebAudio (nao depende de arquivo)
function beep(tipo: 'sucesso' | 'erro' | 'aviso') {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(ctx.destination)
    const freq = tipo === 'sucesso' ? 880 : tipo === 'aviso' ? 600 : 220
    const dur = tipo === 'erro' ? 0.25 : 0.1
    osc.frequency.value = freq
    gain.gain.setValueAtTime(0.2, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur)
    osc.start(); osc.stop(ctx.currentTime + dur)
  } catch {}
}

// ============================================================
// Main
// ============================================================
export default function InventarioDetalhePage() {
  const { id } = useParams<{ id: string }>()
  const invId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [aba, setAba] = useState<'contagem' | 'divergencias' | 'aprovacao' | 'historico'>('contagem')
  const [busca, setBusca] = useState('')
  const [bipValue, setBipValue] = useState('')
  const bipRef = useRef<HTMLInputElement | null>(null)

  const { data: inv, isLoading } = useQuery<InvDetalhe>({
    queryKey: ['inventario', invId],
    queryFn: async () => (await api.get(`/inventarios/${invId}`)).data,
    refetchInterval: (q) => {
      const d = q.state.data as InvDetalhe | undefined
      return d && ['em_contagem', 'conferencia'].includes(d.status) ? 10000 : false
    },
  })

  // Foca input de bipagem no load e ao trocar pra aba contagem
  useEffect(() => {
    if (aba === 'contagem' && inv?.status === 'em_contagem') {
      bipRef.current?.focus()
    }
  }, [aba, inv?.status])

  // Atalho F2 foca bipagem / Esc limpa
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'F2') {
        e.preventDefault()
        bipRef.current?.focus()
      }
      if (e.key === 'Escape' && document.activeElement === bipRef.current) {
        setBipValue('')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ============================================================
  // Mutations
  // ============================================================
  const bipar = useMutation({
    mutationFn: async (data: { codigo: string; qtd: number }) => {
      return (await api.post(`/inventarios/${invId}/bipar`, {
        codigo_ou_ean: data.codigo,
        quantidade: data.qtd,
      })).data
    },
    onSuccess: (r) => {
      if (r.ok) {
        beep('sucesso')
        toast.success(r.mensagem, { duration: 1500 })
      } else {
        beep('erro')
        toast.error(r.mensagem)
      }
      setBipValue('')
      bipRef.current?.focus()
      qc.invalidateQueries({ queryKey: ['inventario', invId] })
    },
    onError: (e: any) => {
      beep('erro')
      toast.error(e?.response?.data?.detail || 'Erro ao bipar')
    },
  })

  const iniciar = useMutation({
    mutationFn: async () => (await api.post(`/inventarios/${invId}/iniciar`)).data,
    onSuccess: () => {
      toast.success('Inventário iniciado — produtos bloqueados pra venda')
      qc.invalidateQueries({ queryKey: ['inventario', invId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao iniciar'),
  })

  const finalizarContagem = useMutation({
    mutationFn: async () => (await api.post(`/inventarios/${invId}/finalizar-contagem`)).data,
    onSuccess: () => {
      toast.success('Contagem finalizada, revise e aprove')
      setAba('aprovacao')
      qc.invalidateQueries({ queryKey: ['inventario', invId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const aprovar = useMutation({
    mutationFn: async () => (await api.post(`/inventarios/${invId}/aprovar`, { aprovar_todos: true })).data,
    onSuccess: () => {
      toast.success('Inventário aprovado e ajustes aplicados')
      qc.invalidateQueries({ queryKey: ['inventario', invId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao aprovar'),
  })

  const cancelar = useMutation({
    mutationFn: async () => (await api.post(`/inventarios/${invId}/cancelar`)).data,
    onSuccess: () => {
      toast.success('Inventário cancelado')
      qc.invalidateQueries({ queryKey: ['inventario', invId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const zerarItem = useMutation({
    mutationFn: async (itemId: number) =>
      (await api.post(`/inventarios/${invId}/itens/${itemId}/zerar`)).data,
    onSuccess: () => {
      toast.success('Item zerado')
      qc.invalidateQueries({ queryKey: ['inventario', invId] })
    },
  })

  const ajusteManual = useMutation({
    mutationFn: async (data: { itemId: number; qtd: number }) =>
      (await api.patch(`/inventarios/${invId}/itens/${data.itemId}`, {
        quantidade_contada: data.qtd,
      })).data,
    onSuccess: () => {
      toast.success('Quantidade ajustada')
      qc.invalidateQueries({ queryKey: ['inventario', invId] })
    },
  })

  // ============================================================
  // Derived
  // ============================================================
  const itensFiltrados = useMemo(() => {
    if (!inv) return []
    const q = busca.trim().toLowerCase()
    if (!q) return inv.itens
    return inv.itens.filter(i =>
      (i.produto_codigo || '').toLowerCase().includes(q) ||
      (i.produto_descricao || '').toLowerCase().includes(q) ||
      (i.produto_ean || '').toLowerCase().includes(q)
    )
  }, [inv, busca])

  const divergentes = useMemo(
    () => inv?.itens.filter(i => i.status === 'divergente') || [],
    [inv]
  )

  const stCfg = inv ? STATUS_CFG[inv.status] || STATUS_CFG.rascunho : null
  const emContagem = inv?.status === 'em_contagem'
  const emConferencia = inv?.status === 'conferencia'
  const finalizado = inv && ['finalizado', 'aprovado', 'cancelado'].includes(inv.status)

  function handleBipar(e: React.FormEvent) {
    e.preventDefault()
    const v = bipValue.trim()
    if (!v) return
    bipar.mutate({ codigo: v, qtd: 1 })
  }

  // ============================================================
  // Render
  // ============================================================
  if (isLoading || !inv) {
    return (
      <div className="p-8 text-center text-ink-500">
        <Clock size={24} className="inline-block animate-pulse" /> Carregando...
      </div>
    )
  }

  const pctContado = inv.total_itens > 0
    ? Math.round((inv.total_contados / inv.total_itens) * 100)
    : 0

  return (
    <div className="space-y-4">
      {/* ============================================================ */}
      {/* Header */}
      {/* ============================================================ */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/estoque/inventarios')} className="text-xs text-ink-500 hover:underline flex items-center gap-1 mb-1">
            <ArrowLeft size={12} /> Inventários
          </button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList size={22} /> {inv.codigo}
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${stCfg?.cls}`}>
              {stCfg?.label}
            </span>
            {inv.modo_cego && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
                <EyeOff size={11} /> Cego
              </span>
            )}
          </h1>
          <div className="text-sm text-ink-600 mt-1 flex flex-wrap items-center gap-3">
            {inv.descricao && <span>{inv.descricao}</span>}
            <span className="flex items-center gap-1"><Warehouse size={11} /> {inv.deposito_nome}</span>
            <span className="capitalize">Tipo: {inv.tipo}</span>
          </div>
        </div>

        {/* Acoes contextuais */}
        <div className="flex items-center gap-2">
          {inv.status === 'rascunho' && (
            <>
              <button onClick={() => cancelar.mutate()} className="btn btn-ghost text-red-600">
                <XCircle size={14} /> Cancelar
              </button>
              <button onClick={() => iniciar.mutate()} disabled={iniciar.isPending}
                className="btn btn-primary flex items-center gap-2">
                <Play size={14} /> Iniciar contagem
              </button>
            </>
          )}
          {emContagem && (
            <>
              <button onClick={() => cancelar.mutate()} className="btn btn-ghost text-red-600">
                <XCircle size={14} /> Cancelar
              </button>
              <button onClick={() => finalizarContagem.mutate()} disabled={finalizarContagem.isPending}
                className="btn btn-primary flex items-center gap-2">
                <FileCheck2 size={14} /> Finalizar contagem
              </button>
            </>
          )}
          {emConferencia && (
            <button onClick={() => { if (confirm('Aplicar ajustes e finalizar?')) aprovar.mutate() }}
              disabled={aprovar.isPending}
              className="btn btn-primary flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle2 size={14} /> Aprovar e aplicar
            </button>
          )}
        </div>
      </div>

      {/* KPIs + progresso */}
      <div className="card card-body">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-xs text-ink-500 uppercase tracking-wide">Itens</div>
            <div className="text-2xl font-bold">{inv.total_contados}<span className="text-ink-400 text-base">/{inv.total_itens}</span></div>
            <div className="text-xs text-ink-500">{pctContado}% contados</div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase tracking-wide">Divergentes</div>
            <div className={`text-2xl font-bold ${inv.total_divergentes > 0 ? 'text-amber-600' : 'text-ink-700'}`}>
              {inv.total_divergentes}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase tracking-wide">Valor divergência</div>
            <div className="text-2xl font-bold text-amber-700">{formatMoney(Number(inv.valor_divergencia))}</div>
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase tracking-wide">Iniciado</div>
            <div className="text-sm font-medium">{inv.iniciado_em ? formatDateTime(inv.iniciado_em) : '—'}</div>
            {inv.aprovado_em && <div className="text-xs text-emerald-700 mt-0.5">Aprovado: {formatDateTime(inv.aprovado_em)}</div>}
          </div>
        </div>
        {/* Barra de progresso */}
        <div className="mt-3 h-2 bg-ink-100 rounded overflow-hidden">
          <div className="h-full bg-brand-500 transition-all" style={{ width: `${pctContado}%` }} />
        </div>
      </div>

      {/* ============================================================ */}
      {/* Abas */}
      {/* ============================================================ */}
      <div className="card overflow-hidden">
        <div className="flex border-b border-ink-200 bg-ink-50">
          <TabBtn active={aba === 'contagem'} onClick={() => setAba('contagem')} icon={Scan} label="Contagem" />
          <TabBtn active={aba === 'divergencias'} onClick={() => setAba('divergencias')} icon={AlertTriangle} label={`Divergências (${divergentes.length})`} />
          {(emConferencia || finalizado) && (
            <TabBtn active={aba === 'aprovacao'} onClick={() => setAba('aprovacao')} icon={FileCheck2} label="Aprovação" />
          )}
          <TabBtn active={aba === 'historico'} onClick={() => setAba('historico')} icon={History} label="Histórico" />
        </div>

        {/* =========== ABA CONTAGEM =========== */}
        {aba === 'contagem' && (
          <div className="p-4 space-y-3">
            {emContagem ? (
              <form onSubmit={handleBipar} className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Scan size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                  <input
                    ref={bipRef}
                    autoFocus
                    value={bipValue}
                    onChange={e => setBipValue(e.target.value)}
                    className="input w-full pl-10 pr-24 text-lg font-mono"
                    placeholder="Bipar EAN ou código (F2 pra focar, Enter pra bipar)"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-400 bg-ink-100 px-2 py-0.5 rounded">F2</span>
                </div>
                <button type="submit" disabled={!bipValue.trim() || bipar.isPending}
                  className="btn btn-primary px-6">
                  Bipar
                </button>
              </form>
            ) : inv.status === 'rascunho' ? (
              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-800 flex items-start gap-2">
                <Info size={16} className="mt-0.5" />
                <div>Inventário em rascunho. Clique em <strong>Iniciar contagem</strong> pra congelar o estoque e habilitar a bipagem.</div>
              </div>
            ) : (
              <div className="bg-ink-50 border border-ink-200 rounded p-3 text-sm text-ink-600 flex items-start gap-2">
                <Info size={16} className="mt-0.5" />
                <div>Bipagem desabilitada: inventário está em <strong>{stCfg?.label}</strong>.</div>
              </div>
            )}

            {/* Busca + lista */}
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                <input
                  value={busca}
                  onChange={e => setBusca(e.target.value)}
                  placeholder="Filtrar por código, descrição ou EAN..."
                  className="input w-full pl-9 input-sm"
                />
              </div>
              <div className="text-xs text-ink-500">{itensFiltrados.length} item(ns)</div>
            </div>

            <TabelaItens
              itens={itensFiltrados}
              modoCego={inv.modo_cego}
              editavel={emContagem || emConferencia}
              onZerar={(itemId) => zerarItem.mutate(itemId)}
              onAjustar={(itemId, qtd) => ajusteManual.mutate({ itemId, qtd })}
            />
          </div>
        )}

        {/* =========== ABA DIVERGENCIAS =========== */}
        {aba === 'divergencias' && (
          <div className="p-4 space-y-3">
            {divergentes.length === 0 ? (
              <div className="text-center text-ink-500 py-8">
                <CheckCircle2 size={32} className="inline-block text-emerald-500 mb-2" />
                <div>Nenhuma divergência até agora.</div>
              </div>
            ) : (
              <TabelaItens
                itens={divergentes}
                modoCego={inv.modo_cego}
                editavel={emContagem || emConferencia}
                onZerar={(itemId) => zerarItem.mutate(itemId)}
                onAjustar={(itemId, qtd) => ajusteManual.mutate({ itemId, qtd })}
              />
            )}
          </div>
        )}

        {/* =========== ABA APROVACAO =========== */}
        {aba === 'aprovacao' && (emConferencia || finalizado) && (
          <PreviewAprovacao invId={invId} finalizado={!!finalizado} />
        )}

        {/* =========== ABA HISTORICO =========== */}
        {aba === 'historico' && <HistoricoInfo inv={inv} />}
      </div>
    </div>
  )
}

// ============================================================
// Subcomponents
// ============================================================
function TabBtn({ active, onClick, icon: Icon, label }: any) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition ${
        active
          ? 'border-brand-500 text-brand-700 bg-white'
          : 'border-transparent text-ink-600 hover:text-ink-800'
      }`}
    >
      <Icon size={14} /> {label}
    </button>
  )
}

function TabelaItens({
  itens, modoCego, editavel, onZerar, onAjustar,
}: {
  itens: InvItem[]
  modoCego: boolean
  editavel: boolean
  onZerar: (itemId: number) => void
  onAjustar: (itemId: number, qtd: number) => void
}) {
  const [editando, setEditando] = useState<number | null>(null)
  const [valorEdit, setValorEdit] = useState<string>('')

  function iniciarEdit(item: InvItem) {
    setEditando(item.id)
    setValorEdit(String(num(item.quantidade_contada)))
  }

  function salvarEdit(itemId: number) {
    const n = parseFloat(valorEdit)
    if (!isNaN(n) && n >= 0) {
      onAjustar(itemId, n)
    }
    setEditando(null)
  }

  if (itens.length === 0) {
    return <div className="text-center text-ink-500 py-8">Nenhum item encontrado.</div>
  }

  return (
    <div className="overflow-x-auto">
      <table className="table text-sm">
        <thead>
          <tr>
            <th>Código</th>
            <th>Descrição</th>
            <th className="text-right">Esperado</th>
            <th className="text-right">Contado</th>
            <th className="text-right">Divergência</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {itens.map(it => {
            const cfg = ITEM_STATUS_CFG[it.status] || ITEM_STATUS_CFG.pendente
            const diff = num(it.quantidade_contada) - num(it.quantidade_esperada)
            const valorDiff = diff * num(it.custo_medio_snapshot)
            return (
              <tr key={it.id}>
                <td className="font-mono text-xs">{it.produto_codigo}</td>
                <td>
                  {it.produto_descricao}
                  {it.produto_endereco && <span className="ml-2 text-xs text-ink-500">· {it.produto_endereco}</span>}
                </td>
                <td className="text-right">
                  {modoCego ? (
                    <span className="text-ink-300 italic">oculto</span>
                  ) : (
                    <span>{num(it.quantidade_esperada).toFixed(3).replace(/\.?0+$/, '')}</span>
                  )}
                </td>
                <td className="text-right">
                  {editando === it.id ? (
                    <input
                      type="number"
                      step="any"
                      autoFocus
                      value={valorEdit}
                      onChange={e => setValorEdit(e.target.value)}
                      onBlur={() => salvarEdit(it.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') salvarEdit(it.id)
                        if (e.key === 'Escape') setEditando(null)
                      }}
                      className="input input-sm w-24 text-right"
                    />
                  ) : (
                    <span
                      className={editavel ? 'cursor-pointer hover:bg-ink-100 px-1 rounded' : ''}
                      onClick={() => editavel && iniciarEdit(it)}
                    >
                      {num(it.quantidade_contada).toFixed(3).replace(/\.?0+$/, '')}
                    </span>
                  )}
                </td>
                <td className="text-right">
                  {modoCego && it.status !== 'aprovado' ? (
                    <span className="text-ink-300 italic">oculto</span>
                  ) : diff === 0 ? (
                    <span className="text-ink-400">—</span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 font-semibold ${diff > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      {diff > 0 ? '+' : ''}{diff.toFixed(3).replace(/\.?0+$/, '')}
                      <span className="text-ink-400 text-xs ml-1">({formatMoney(valorDiff)})</span>
                    </span>
                  )}
                </td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
                    {cfg.label}
                  </span>
                </td>
                <td className="text-right">
                  {editavel && num(it.quantidade_contada) > 0 && (
                    <button
                      onClick={() => { if (confirm('Zerar contagem deste item?')) onZerar(it.id) }}
                      className="text-xs text-ink-500 hover:text-red-600 flex items-center gap-1"
                      title="Zerar contagem"
                    >
                      <RotateCcw size={12} />
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function PreviewAprovacao({ invId, finalizado }: { invId: number; finalizado: boolean }) {
  const { data, isLoading } = useQuery<PreviewResp>({
    queryKey: ['inv-preview', invId],
    queryFn: async () => (await api.get(`/inventarios/${invId}/preview`)).data,
  })

  if (isLoading || !data) {
    return <div className="p-8 text-center text-ink-500">Calculando preview...</div>
  }

  return (
    <div className="p-4 space-y-3">
      {!finalizado && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 flex items-start gap-2 text-sm text-blue-900">
          <Info size={16} className="mt-0.5" />
          <div>
            Revise os ajustes antes de aprovar. Ao confirmar, os estoques serão atualizados e os produtos voltarão a permitir venda.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase">Produtos com ajuste</div>
          <div className="text-2xl font-bold">{data.produtos_com_ajuste}<span className="text-ink-400 text-base">/{data.total_itens}</span></div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase">Valor total (absoluto)</div>
          <div className={`text-2xl font-bold ${Math.abs(data.valor_total_divergencia) > 0 ? 'text-amber-700' : 'text-ink-700'}`}>
            {formatMoney(Math.abs(data.valor_total_divergencia))}
          </div>
        </div>
        <div className="card card-body">
          <div className="text-xs text-ink-500 uppercase">Saldo líquido</div>
          <div className={`text-2xl font-bold ${data.valor_total_divergencia > 0 ? 'text-emerald-700' : data.valor_total_divergencia < 0 ? 'text-red-700' : 'text-ink-700'}`}>
            {data.valor_total_divergencia > 0 ? '+' : ''}{formatMoney(data.valor_total_divergencia)}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="table text-sm">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descrição</th>
              <th className="text-right">Esperado</th>
              <th className="text-right">Final</th>
              <th className="text-right">Ajuste</th>
              <th className="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {data.itens.filter(i => i.divergencia !== 0).map(i => (
              <tr key={i.item_id}>
                <td className="font-mono text-xs">{i.codigo}</td>
                <td>{i.descricao}</td>
                <td className="text-right">{i.esperada}</td>
                <td className="text-right font-semibold">{i.final}</td>
                <td className={`text-right font-semibold ${i.divergencia > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {i.divergencia > 0 ? '+' : ''}{i.divergencia}
                </td>
                <td className={`text-right ${i.valor_divergencia > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatMoney(i.valor_divergencia)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.itens.filter(i => i.divergencia !== 0).length === 0 && (
          <div className="p-6 text-center text-ink-500 text-sm">
            <CheckCircle2 size={20} className="inline-block text-emerald-500 mr-1" />
            Nenhuma divergência — nada a ajustar.
          </div>
        )}
      </div>
    </div>
  )
}

function HistoricoInfo({ inv }: { inv: InvDetalhe }) {
  return (
    <div className="p-4">
      <div className="space-y-2 text-sm">
        <Linha icon={Clock} label="Criado em" value={formatDateTime(inv.created_at)} />
        {inv.iniciado_em && <Linha icon={Play} label="Iniciado em" value={formatDateTime(inv.iniciado_em)} cls="text-blue-700" />}
        {inv.finalizado_em && <Linha icon={FileCheck2} label="Contagem finalizada em" value={formatDateTime(inv.finalizado_em)} cls="text-amber-700" />}
        {inv.aprovado_em && <Linha icon={CheckCircle2} label="Aprovado em" value={formatDateTime(inv.aprovado_em)} cls="text-emerald-700" />}
        {inv.cancelado_em && <Linha icon={XCircle} label="Cancelado em" value={formatDateTime(inv.cancelado_em)} cls="text-red-700" />}
      </div>
      <div className="mt-4 text-xs text-ink-500 italic">
        Log detalhado de bipagens virá em versão futura (cada bipagem é gravada em `inventario_bipagens` pra auditoria).
      </div>
    </div>
  )
}

function Linha({ icon: Icon, label, value, cls }: any) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={14} className={cls || 'text-ink-500'} />
      <span className="text-ink-500 w-44">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}
